import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    ButtonGroup
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { MODAL_BORDER_RADIUS } from '../../utils/dateUtils';

const EditItemModal = ({
    open,
    onClose,
    onUpdate,
    onDelete,
    onEnterMoveMode,
    onPreview,
    formState,
    handleFormChange,
    handleContentTypeChange,
    handleContentSelection,
    handleCollectionSelection,
    simulationData,
    courseData,
    collectionData,
    featureSwitchesApiData,
    style 
}) => {
    const [simulationType, setSimulationType] = useState(null);

    const simulationsEnabled = featureSwitchesApiData?.enableJourneyBuilderSimulations !== false;
    const coursesEnabled = featureSwitchesApiData?.enableJourneyBuilderCourses !== false;
    const bothEnabled = simulationsEnabled && coursesEnabled;
    const onlySimulations = simulationsEnabled && !coursesEnabled;
    const onlyCourses = !simulationsEnabled && coursesEnabled;
    const noneEnabled = !simulationsEnabled && !coursesEnabled;

    useEffect(() => {
        if (!open) {
            setSimulationType(null);
            return;
        }

        if (onlySimulations && formState.contentType !== 'Simulation') {
            const syntheticEvent = {
                target: { value: 'Simulation' }
            };
            handleContentTypeChange(syntheticEvent);
        } else if (onlyCourses && formState.contentType !== 'Course') {
            const syntheticEvent = {
                target: { value: 'Course' }
            };
            handleContentTypeChange(syntheticEvent);
        }
    }, [open, onlySimulations, onlyCourses]);

    useEffect(() => {
        if (!open) {
            setSimulationType(null);
            return;
        }

        if (formState.contentType === 'Simulation') {
            if (simulationType === null) {
                if (formState.selectedContentName === 'Random Simulation' || formState.isRandomSimulation) {
                    setSimulationType('Random');
                } else if (formState.selectedContentName && formState.selectedContentName !== 'Random Simulation') {
                    setSimulationType('Manual');
                }
            }
        } else if (formState.contentType === 'Course') {
            setSimulationType(null);
        }
    }, [open, formState.contentType]);

    const handleContentTypeButtonClick = (contentType) => {
        if (contentType === 'Course' && !coursesEnabled) return;
        if (contentType === 'Simulation' && !simulationsEnabled) return;

        setSimulationType(null);
        const syntheticEvent = {
            target: { value: contentType }
        };
        handleContentTypeChange(syntheticEvent);
    };

    const handleSimulationTypeClick = (type) => {
        setSimulationType(type);
        if (type === 'Random') {
            const syntheticEvent = {
                target: { value: 'Random Simulation' }
            };
            handleContentSelection(syntheticEvent);
        } else {
            const resetEvent = {
                target: { value: '' }
            };
            handleContentSelection(resetEvent);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: MODAL_BORDER_RADIUS,
                    ...style
                },
                sx: { bgcolor: 'background.default' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Edit Item</Typography>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={!formState.contentId}
                        onClick={onPreview}
                        startIcon={<VisibilityIcon />}
                    >
                        Preview
                    </Button>
                </Box>
            </DialogTitle>
            <DialogContent>
                {noneEnabled ? (
                    <Box sx={{ py: 3 }}>
                        <Typography variant="body1" color="text.secondary" align="center">
                            No content types are enabled for Journey Builder. Please contact your administrator.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {/* Content Type Selection Buttons */}
                        <Grid item xs={12} sx={{ mb: -0.1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Content type
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                <Button
                                    variant={formState.contentType === 'Course' ? 'contained' : 'outlined'}
                                    color={formState.contentType === 'Course' ? 'warning' : 'inherit'}
                                    onClick={() => handleContentTypeButtonClick('Course')}
                                    disabled={!coursesEnabled}
                                    sx={{ flex: 1, mr: 1 }}
                                >
                                    Course
                                </Button>
                                <Button
                                    variant={formState.contentType === 'Simulation' ? 'contained' : 'outlined'}
                                    color={formState.contentType === 'Simulation' ? 'primary' : 'inherit'}
                                    onClick={() => handleContentTypeButtonClick('Simulation')}
                                    disabled={!simulationsEnabled}
                                    sx={{ flex: 1, ml: 1 }}
                                >
                                    Simulation
                                </Button>
                            </Box>
                        </Grid>

                        {/* Course Selection */}
                        {formState.contentType === 'Course' && coursesEnabled && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Select Course</InputLabel>
                                    <Select
                                        name="selectedContentName"
                                        value={formState.selectedContentName || ''}
                                        onChange={handleContentSelection}
                                        label="Select Course"
                                        required
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                        }}
                                    >
                                        {(courseData || []).map((course, index) => (
                                            <MenuItem key={`course-${index}`} value={course.courseName}>
                                                {course.courseName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {/* Simulation Type Selection */}
                        {formState.contentType === 'Simulation' && simulationsEnabled && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Simulation selection
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <Button
                                        variant={simulationType === 'Manual' ? 'contained' : 'outlined'}
                                        color={simulationType === 'Manual' ? 'primary' : 'inherit'}
                                        onClick={() => handleSimulationTypeClick('Manual')}
                                        sx={{ flex: 1, mr: 1 }}
                                    >
                                        Manual
                                    </Button>

                                    {featureSwitchesApiData.enableRandomSimulation ? (
                                        <Button
                                            variant={simulationType === 'Random' ? 'contained' : 'outlined'}
                                            color={simulationType === 'Random' ? 'primary' : 'inherit'}
                                            onClick={() => handleSimulationTypeClick('Random')}
                                            sx={{ flex: 1, ml: 1 }}
                                        >
                                            Random
                                        </Button>
                                    ) : (
                                        <Box sx={{ flex: 1, ml: 1 }} /> // Non-interactive placeholder
                                    )}
                                </Box>
                            </Grid>
                        )}

                        {/* Manual Simulation Selection */}
                        {formState.contentType === 'Simulation' && simulationType === 'Manual' && simulationsEnabled && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Select Simulation</InputLabel>
                                    <Select
                                        name="selectedContentName"
                                        value={formState.selectedContentName === 'Random Simulation' ? '' : (formState.selectedContentName || '')}
                                        onChange={handleContentSelection}
                                        label="Select Simulation"
                                        required
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                        }}
                                    >
                                        {(simulationData || []).map((sim, index) => (
                                            <MenuItem key={`sim-${index}`} value={sim.simulationName}>
                                                {sim.simulationName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {/* Random Simulation Collection Selection */}
                        {formState.contentType === 'Simulation' && simulationType === 'Random' && simulationsEnabled && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Select Collection</InputLabel>
                                    <Select
                                        name="selectedCollectionId"
                                        value={formState.contentName || ''}
                                        onChange={handleCollectionSelection}
                                        label="Select Collection"
                                        required
                                    >
                                        {collectionData && collectionData.map((collection, index) => (
                                            <MenuItem key={index} value={collection.collectionName}>
                                                {collection.collectionName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {/* Date Selection */}
                        <Grid item xs={12}>
                            <TextField
                                name="scheduledFor"
                                label="Scheduled For"
                                type="date"
                                value={formState.scheduledFor}
                                onChange={handleFormChange}
                                disabled={true}
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onDelete} color="error" startIcon={<DeleteIcon />} sx={{ mr: 'auto' }}>
                    Delete
                </Button>
                <Button onClick={onClose} variant="contained" color="warning">
                    Cancel
                </Button>
                <Button onClick={onEnterMoveMode} variant="contained" color="secondary" startIcon={<DriveFileMoveIcon />}>
                    Move Item
                </Button>
                <Button
                    onClick={onUpdate}
                    variant="contained"
                    color="primary"
                    disabled={noneEnabled || !formState.contentId || !formState.scheduledFor}
                >
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditItemModal;